import type {
  PayrollPoliciesState,
  StatutoryPayeBand,
} from '../../data/use_payroll_policies';

/**
 * What the Payroll Policies screen should render for a given
 * `PayrollPoliciesState` (#22726). Extracted as a pure, independently-tested
 * function — mirrors `employee_directory_utils.ts`'s
 * `resolve_employee_directory_view` (same "ordering regression -> one unit
 * test away from being caught" rationale): `loading`/`no_entity`/
 * `entitlement_required`/`error` must all be checked before the
 * `ready`-with-unsupported-jurisdiction branch, never the other way around.
 *
 * `unsupported` is distinct from every other non-`ready` state: the state IS
 * `ready` (the fetch succeeded), but the resolved policy itself reports
 * `supported: false` (the entity's jurisdiction has no registered statutory
 * policy content — WagePro is SL-only today, but `entities.country` is a
 * free column).
 */
export type PayrollPoliciesView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'unsupported'
  | 'ready';

export function resolve_payroll_policies_view(
  state: PayrollPoliciesState,
): PayrollPoliciesView {
  if (state.status === 'loading') return 'loading';
  if (state.status === 'no_entity') return 'no_entity';
  if (state.status === 'entitlement_required') return 'entitlement_required';
  if (state.status === 'error') return 'error';
  // state.status === 'ready'
  return state.policy.supported ? 'ready' : 'unsupported';
}

/** The "Monthly regular profits" cell of one printed IRD APIT row. Amounts are cents, never fractional-edge values. */
export type ApitIncomeLabel =
  | { kind: 'up_to'; to: number }
  | { kind: 'between'; from: number; to: number }
  | { kind: 'exceeding'; from: number };

/** The "Tax" cell of one printed IRD APIT row. */
export type ApitTaxLabel =
  | { kind: 'relief' }
  | { kind: 'rate_only'; rate_bps: number }
  | { kind: 'formula'; rate_bps: number; subtrahend_cents: number };

/**
 * Projects one engine band onto the IRD's PRINTED row ("exceeding Rs 150,000
 * but not exceeding Rs 233,333 -> 6% less Rs 9,000"), generic over however
 * many rows the table in force has.
 *
 * The "exceeding" edge is the PREVIOUS band's `to_cents`, never
 * `from_cents - 1`: the engine's `from_cents` is an inclusive cent edge
 * (150,000.01) that the IRD never prints, so for a well-formed table (every
 * band after the first preceded by a bounded band) no fractional-cent edge
 * reaches the DOM. Only a malformed table (a band after an open-ended one)
 * falls back to the engine's own `from_cents`.
 */
export function describe_apit_band(
  previous: StatutoryPayeBand | undefined,
  band: StatutoryPayeBand,
): { income: ApitIncomeLabel; tax: ApitTaxLabel } {
  let income: ApitIncomeLabel;
  if (previous === undefined) {
    income =
      band.to_cents === null
        ? { kind: 'exceeding', from: 0 }
        : { kind: 'up_to', to: band.to_cents };
  } else if (previous.to_cents === null) {
    // Malformed table (a band after an open-ended one): fall back to the
    // engine's own lower edge rather than inventing one.
    income = { kind: 'exceeding', from: band.from_cents };
  } else if (band.to_cents === null) {
    income = { kind: 'exceeding', from: previous.to_cents };
  } else {
    income = { kind: 'between', from: previous.to_cents, to: band.to_cents };
  }

  let tax: ApitTaxLabel;
  if (band.rate_bps === 0 && band.subtrahend_cents === 0) {
    tax = { kind: 'relief' };
  } else if (band.subtrahend_cents === 0) {
    tax = { kind: 'rate_only', rate_bps: band.rate_bps };
  } else {
    tax = {
      kind: 'formula',
      rate_bps: band.rate_bps,
      subtrahend_cents: band.subtrahend_cents,
    };
  }
  return { income, tax };
}
